import React from 'react';
import '../../css/main.css';
import API from '../../api/api';
import LinearProgress from '@mui/material/LinearProgress';
import { Component } from 'react';
import SecuredZone from '../../components/SecuredZone';
import linkState from 'linkstate';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';

const FireApi = new API();

class User extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      user: {
        id: null,
        email: '',
        username: '',
      },
      password: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      passwordError: '',
      passwordSuccess: false,
    };
  }

  componentDidMount() {
    this.getUser();
  }

  getUser() {
    FireApi.endpoints.me.getAll()
      .then((userGet) => {
        // Handle both response structures: {success: true, user: {...}} or array
        let userData = null;
        if (userGet.data && userGet.data.user) {
          // Response structure: {success: true, user: {...}}
          userData = userGet.data.user;
        } else if (userGet.data && Array.isArray(userGet.data) && userGet.data.length > 0) {
          // Response structure: [{...}]
          userData = userGet.data[0];
        } else if (userGet.data && userGet.data.id) {
          // Response structure: {...} (direct user object)
          userData = userGet.data;
        }
        
        if (userData) {
          this.setState({ 
            user: {
              ...userData,
            },
            loading: false 
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  handlePasswordChange(field, value) {
    this.setState({
      password: {
        ...this.state.password,
        [field]: value,
      },
      passwordError: '',
      passwordSuccess: false,
    });
  }

  validatePassword() {
    if (!this.state.password.currentPassword) {
      this.setState({ passwordError: 'Aktuelles Passwort ist erforderlich' });
      return false;
    }
    if (!this.state.password.newPassword) {
      this.setState({ passwordError: 'Neues Passwort ist erforderlich' });
      return false;
    }
    if (this.state.password.newPassword.length < 8) {
      this.setState({ passwordError: 'Neues Passwort muss mindestens 8 Zeichen lang sein' });
      return false;
    }
    if (this.state.password.newPassword !== this.state.password.confirmPassword) {
      this.setState({ passwordError: 'Passwörter stimmen nicht überein' });
      return false;
    }
    return true;
  }

  updatePassword() {
    if (!this.validatePassword()) {
      return;
    }

    this.setState({ loading: true, passwordError: '', passwordSuccess: false });

    // Use PUT /v2/password/ endpoint for password change
    const passwordData = {
      oldPassword: this.state.password.currentPassword,
      newPassword: this.state.password.newPassword,
      newPasswordConfirm: this.state.password.confirmPassword,
    };

    // Use password endpoint (no ID needed)
    FireApi.endpoints.password.update(
      passwordData,
      { withCredentials: true, crossDomain: true }
    )
      .then(() => {
        this.setState({ 
          loading: false,
          passwordSuccess: true,
          password: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          }
        });
        setTimeout(() => {
          this.setState({ passwordSuccess: false });
        }, 3000);
      })
      .catch(() => {
        this.setState({ 
          loading: false,
          passwordError: 'Fehler beim Ändern des Passworts. Bitte überprüfen Sie Ihr aktuelles Passwort.'
        });
      });
  }

  updateUser() {
    this.setState({ loading: true });

    const userData = {
      id: this.state.user.id,
      email: this.state.user.email || '',
      username: this.state.user.username || '',
      verified: this.state.user.verified !== undefined ? this.state.user.verified : 0,
      resettable: this.state.user.resettable !== undefined ? this.state.user.resettable : 1,
      force_logout: this.state.user.force_logout !== undefined ? this.state.user.force_logout : 0,
      organisation_id: this.state.user.organisation_id || null,
    };

    FireApi.endpoints.me.update(userData)
      .then(() => {
        this.setState({ loading: false });
        this.getUser();
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  render() {
    return (
      <SecuredZone 
        title="Benutzer"
        breadcrumbs={{ '/': 'Dashboard', '/user': 'Benutzer' }}
        loading={this.state.loading ? <LinearProgress color="primary" /> : <span className="loader-span"></span>}
      >
        <div className="settings-form">
          <Typography variant="h6" className="settings-section-title">
            Persönliche Informationen
          </Typography>

          <div className="user-input-linoen">
            <TextField
              autoFocus
              margin="dense"
              id="username"
              label="Benutzername"
              type="text"
              fullWidth
              variant="outlined"
              value={this.state.user.username || ''}
              onInput={linkState(this, 'user.username')}
            />
          </div>

          <div className="user-input-linoen">
            <TextField
              margin="dense"
              id="email"
              label="E-Mail Adresse"
              type="email"
              fullWidth
              variant="outlined"
              value={this.state.user.email || ''}
              onInput={linkState(this, 'user.email')}
            />
          </div>

          {/* Account Information Section (Read-only) */}
          <Paper elevation={0} className="settings-info-panel">
            <Typography variant="subtitle1" className="settings-section-title">
              Kontoinformationen
            </Typography>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <Typography 
                  variant="caption" 
                  style={{ 
                    color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#d1d5db' : '#666',
                    transition: 'color 0.3s ease'
                  }}
                >
                  Verifiziert
                </Typography>
                <div style={{ marginTop: '5px' }}>
                  {this.state.user.verified === 1 ? (
                    <Chip label="Ja" color="success" size="small" />
                  ) : (
                    <Chip label="Nein" color="default" size="small" />
                  )}
                </div>
              </div>
              <div>
                <Typography 
                  variant="caption" 
                  style={{ 
                    color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#d1d5db' : '#666',
                    transition: 'color 0.3s ease'
                  }}
                >
                  Passwort zurücksetzbar
                </Typography>
                <div style={{ marginTop: '5px' }}>
                  {this.state.user.resettable === 1 ? (
                    <Chip label="Ja" color="success" size="small" />
                  ) : (
                    <Chip label="Nein" color="default" size="small" />
                  )}
                </div>
              </div>
              {this.state.user.organisation_id && (
                <div>
                  <Typography 
                    variant="caption" 
                    style={{ 
                      color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#d1d5db' : '#666',
                      transition: 'color 0.3s ease'
                    }}
                  >
                    Organisations-ID
                  </Typography>
                  <div style={{ 
                    marginTop: '5px', 
                    fontSize: '14px',
                    color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#ffffff' : '#1a1a1a',
                    transition: 'color 0.3s ease'
                  }}>
                    {this.state.user.organisation_id}
                  </div>
                </div>
              )}
              {this.state.user.registered && (
                <div>
                  <Typography 
                    variant="caption" 
                    style={{ 
                      color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#d1d5db' : '#666',
                      transition: 'color 0.3s ease'
                    }}
                  >
                    Registriert
                  </Typography>
                  <div style={{ 
                    marginTop: '5px', 
                    fontSize: '14px',
                    color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#ffffff' : '#1a1a1a',
                    transition: 'color 0.3s ease'
                  }}>
                    {new Date(this.state.user.registered * 1000).toLocaleString('de-DE')}
                  </div>
                </div>
              )}
              {this.state.user.last_login && (
                <div>
                  <Typography 
                    variant="caption" 
                    style={{ 
                      color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#d1d5db' : '#666',
                      transition: 'color 0.3s ease'
                    }}
                  >
                    Letzter Login
                  </Typography>
                  <div style={{ 
                    marginTop: '5px', 
                    fontSize: '14px',
                    color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#ffffff' : '#1a1a1a',
                    transition: 'color 0.3s ease'
                  }}>
                    {new Date(this.state.user.last_login * 1000).toLocaleString('de-DE')}
                  </div>
                </div>
              )}
            </div>
          </Paper>

          {/* Save Button for Personal Information */}
          <div className="user-input-boetton" style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => { this.updateUser(); }}
              size="large"
            >
              Informationen speichern
            </Button>
          </div>

          <Divider style={{ margin: '40px 0' }} />

          {/* Password Change Section */}
          <Typography variant="h6" className="settings-section-title">
            Passwort ändern
          </Typography>

          {this.state.passwordError && (
            <div className="settings-span-2" style={{ 
              padding: '10px', 
              backgroundColor: '#ffebee', 
              color: '#c62828', 
              borderRadius: '4px' 
            }}>
              {this.state.passwordError}
            </div>
          )}

          {this.state.passwordSuccess && (
            <div className="settings-span-2" style={{ 
              padding: '10px', 
              backgroundColor: '#e8f5e9', 
              color: '#2e7d32', 
              borderRadius: '4px' 
            }}>
              Passwort erfolgreich geändert!
            </div>
          )}

          <div className="user-input-linoen">
            <TextField
              margin="dense"
              id="currentPassword"
              label="Aktuelles Passwort"
              type={this.state.showCurrentPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={this.state.password.currentPassword}
              onChange={(e) => this.handlePasswordChange('currentPassword', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => this.setState({ showCurrentPassword: !this.state.showCurrentPassword })}
                      edge="end"
                    >
                      {this.state.showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="user-input-linoen">
            <TextField
              margin="dense"
              id="newPassword"
              label="Neues Passwort"
              type={this.state.showNewPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={this.state.password.newPassword}
              onChange={(e) => this.handlePasswordChange('newPassword', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => this.setState({ showNewPassword: !this.state.showNewPassword })}
                      edge="end"
                    >
                      {this.state.showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="user-input-linoen settings-span-2">
            <TextField
              margin="dense"
              id="confirmPassword"
              label="Neues Passwort bestätigen"
              type={this.state.showConfirmPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={this.state.password.confirmPassword}
              onChange={(e) => this.handlePasswordChange('confirmPassword', e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => this.setState({ showConfirmPassword: !this.state.showConfirmPassword })}
                      edge="end"
                    >
                      {this.state.showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {/* Save Button for Password */}
          <div className="user-input-boetton" style={{ display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => { this.updatePassword(); }}
              size="large"
            >
              Passwort ändern
            </Button>
          </div>
        </div>
      </SecuredZone>
    );
  }
}

export default User;
